import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from "chart.js";
import type { ChartOptions } from "chart.js";
import { Scatter } from "react-chartjs-2";
import type { DoubleGap } from "../../types/doubleStats";
import {
  ACTUAL_COLOR,
  GRID_COLOR,
  TICK_COLOR,
  dateAxis,
  fmtFullDate,
  winterBands
} from "./chartHelpers";

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

// Doubles averaged into each point of the trend line (~2 months).
const ROLLING_WINDOW = 12;

type Props = {
  gaps: DoubleGap[];
  avgDaysAll: number | null;
};

const GapsChart = ({ gaps, avgDaysAll }: Props) => {
  const points = gaps.map((g) => ({
    x: new Date(g.crossedAt).getTime(),
    y: g.days,
    milestone: g.milestone
  }));
  const rolling = points.slice(ROLLING_WINDOW - 1).map((_, i) => {
    const window = points.slice(i, i + ROLLING_WINDOW);
    return {
      x: window[window.length - 1].x,
      y: window.reduce((sum, p) => sum + p.y, 0) / window.length
    };
  });
  const minX = points[0]?.x;
  const maxX = points[points.length - 1]?.x;

  const options: ChartOptions<"scatter"> = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1.9,
    scales: {
      x: dateAxis(6),
      y: {
        suggestedMin: 2,
        ticks: { color: TICK_COLOR, callback: (v) => `${v}d` },
        grid: { color: GRID_COLOR }
      }
    },
    plugins: {
      legend: {
        display: true,
        labels: {
          color: TICK_COLOR,
          filter: (item) => item.text !== "All-time average"
        }
      },
      tooltip: {
        callbacks: {
          title: (items) => fmtFullDate(items[0].parsed.x ?? 0),
          label: (item) => {
            const raw = item.raw as { milestone?: number };
            const days = `${(item.parsed.y ?? 0).toFixed(2)} days`;
            return raw.milestone
              ? `${days} to ${(raw.milestone / 1e9).toFixed(3)}B`
              : `${item.dataset.label}: ${days}`;
          }
        }
      }
    }
  };

  return (
    <Scatter
      options={options}
      plugins={[winterBands]}
      data={{
        datasets: [
          {
            label: "Days since previous double",
            data: points,
            backgroundColor: "rgba(152, 216, 255, 0.4)",
            pointRadius: 3
          },
          {
            label: `${ROLLING_WINDOW}-double average`,
            data: rolling,
            showLine: true,
            borderColor: ACTUAL_COLOR,
            backgroundColor: ACTUAL_COLOR,
            borderWidth: 2.5,
            pointRadius: 0,
            pointHitRadius: 6
          },
          ...(avgDaysAll !== null && minX !== undefined
            ? [
                {
                  label: "All-time average",
                  data: [
                    { x: minX, y: avgDaysAll },
                    { x: maxX, y: avgDaysAll }
                  ],
                  showLine: true,
                  borderColor: "rgba(199, 238, 255, 0.45)",
                  borderDash: [5, 4],
                  borderWidth: 1,
                  pointRadius: 0,
                  pointHitRadius: 0
                }
              ]
            : [])
        ]
      }}
    />
  );
};

export default GapsChart;
